const MARKET_CONTEXT_ENDPOINT = '/api/market-context';
const RESOLVED_TTL_MS = 6 * 60 * 60 * 1000;
const UNAVAILABLE_TTL_MS = 60 * 1000;
const REQUEST_TIMEOUT_MS = 2500;
const INVALID_COUNTRY_CODES = new Set(['XX', 'T1', 'A1', 'A2']);

interface MarketContextResponse {
  country?: unknown;
}

interface CachedCountry {
  country: string | null;
  expiresAt: number;
}

let cachedCountry: CachedCountry | null = null;
let countryRequest: Promise<string | null> | null = null;

export function normalizeMarketCountry(rawCountry: unknown): string | null {
  const country = String(rawCountry || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(country) || INVALID_COUNTRY_CODES.has(country)) {
    return null;
  }
  return country;
}

async function requestMarketCountry(): Promise<string | null> {
  if (typeof fetch !== 'function' || typeof window === 'undefined') return null;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(MARKET_CONTEXT_ENDPOINT, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const payload = await response.json() as MarketContextResponse;
    return normalizeMarketCountry(payload.country);
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function getMarketCountry(): Promise<string | null> {
  const now = Date.now();
  if (cachedCountry && cachedCountry.expiresAt > now) {
    return cachedCountry.country;
  }

  if (!countryRequest) {
    countryRequest = requestMarketCountry().then((country) => {
      cachedCountry = {
        country,
        expiresAt: Date.now() + (country ? RESOLVED_TTL_MS : UNAVAILABLE_TTL_MS),
      };
      return country;
    }).finally(() => {
      countryRequest = null;
    });
  }

  return countryRequest;
}

export async function buildMarketRequestHeaders(
  language: string,
  initialHeaders?: HeadersInit,
): Promise<Record<string, string>> {
  const headers = new Headers(initialHeaders);
  headers.set('x-user-lang', String(language || '').toLowerCase().startsWith('fr') ? 'fr' : 'en');

  const country = await getMarketCountry();
  if (country) headers.set('x-country-code', country);
  const result: Record<string, string> = {};
  headers.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

export function _resetMarketContextForTests(): void {
  cachedCountry = null;
  countryRequest = null;
}
